'use client';

import { useScheduler } from '../SchedulerContext';
import { Alert, Card, CardBody, Image, Spinner, Button } from '@heroui/react';
import { FaCheckCircle } from 'react-icons/fa';
import { IoWarningOutline } from 'react-icons/io5';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { PropertyDetails } from '../SchedulerContext';
import AddressSearchInputFallbackForm from '@/components/forms/AddressSearchInputFallbackForm';
import PropertyFormField from './components/PropertyFormField';
import VerificationWarnings from './components/VerificationWarnings';
import { PROPERTY_FIELDS } from './propertyFormConfig';
import {
  formatFieldList,
  getRequiredPropertyFieldIssues,
  getRequiredPropertyFieldPrompt,
  getUnverifiedAddressFieldLabels,
} from './propertyRequirements';

type PropertyReviewProps = {
  onContinue: () => void;
};

export default function PropertyReview({ onContinue }: PropertyReviewProps) {
  const {
    address,
    setAddress,
    streetViewUrl,
    zillowPropertyData,
    containingServiceAreaArray,
    propertyDetails,
    setPropertyDetails,
    companyStyles,
    jwtToken,
  } = useScheduler();

  const defaultPropertyDetails: PropertyDetails = {
    bedrooms: undefined,
    bedroomsVerified: true,
    bathrooms: undefined,
    bathroomsVerified: true,
    foundationType: undefined,
    foundationTypeVerified: true,
    waterType: undefined,
    waterTypeVerified: true,
    sewerType: undefined,
    sewerTypeVerified: true,
    poolCount: 0,
    poolCountVerified: true,
    garageCount: 0,
    garageCountVerified: true,
    outbuildingCount: 0,
    outbuildingCountVerified: true,
  };

  const { register, control, watch, setValue, reset } =
    useForm<PropertyDetails>({
      defaultValues: propertyDetails || defaultPropertyDetails,
    });

  // Reset form when propertyDetails changes
  useEffect(() => {
    if (propertyDetails) {
      reset(propertyDetails);
    } else {
      reset(defaultPropertyDetails);
    }
  }, [propertyDetails, reset]);

  // Auto-verify fields when user edits them - configuration-driven
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === 'change' && name) {
        // Find the field config
        const field = PROPERTY_FIELDS.find((f) => f.name === name);
        if (field) {
          // Auto-verify the field
          setValue(field.verifiedField, true as any, { shouldDirty: false });
        }

        // Update context with new values
        const currentValues = watch();
        setPropertyDetails(currentValues as PropertyDetails);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, setPropertyDetails]);

  if (!address) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <p className='text-gray-500'>No property information available</p>
      </div>
    );
  }

  // Square Footage and Year Built block progression alongside
  // Foundation. Empty and zero both count as missing — a zero sqft (the default
  // for new construction, which Zillow has no data for) silently misprices the
  // job. Number of Units stays warning-only and is deliberately excluded.
  //
  // Each issue carries its own sentence, because "required" is not always the
  // truth: a Square Footage of 59 is present but below the floor, and being told
  // it is required while looking straight at it explains nothing.
  const requiredFieldIssues = getRequiredPropertyFieldIssues(address);
  const hasMissingRequiredAddressData = requiredFieldIssues.length > 0;
  const hasOutOfRangeRequiredField = requiredFieldIssues.some(
    (issue) => issue.reason === 'out-of-range'
  );
  const requiredFieldPrompt = getRequiredPropertyFieldPrompt(address);

  // Advisory only: fields holding a usable value that Zillow guessed at. A
  // required field that is missing surfaces as a blocking error instead, so it
  // is never reported twice.
  const unverifiedAddressFieldLabels = getUnverifiedAddressFieldLabels(address);
  const hasUnverifiedAddressData = unverifiedAddressFieldLabels.length > 0;

  const hasUnverifiedPropertyDetails = propertyDetails
    ? PROPERTY_FIELDS.some((field) => {
        const value = propertyDetails[field.name];
        const isVerified = propertyDetails[field.verifiedField];
        return field.showWarningAlways
          ? !isVerified
          : value !== undefined && !isVerified;
      })
    : false;

  // console.log(containingServiceAreaArray);

  return (
    <div className='mx-auto max-w-5xl space-y-6'>
      <div className='text-center'>
        <h4 className='mb-2'>Review Your Property</h4>
        <p className='text-sm text-gray-600'>
          Please confirm the information below is correct
        </p>
        <p className='mt-1 text-xs text-gray-500'>
          Pricing may change if property information is inaccurate.
        </p>
      </div>

      {/* Street View Image */}
      <div className='relative mx-auto flex max-w-md justify-center'>
        <Image
          isBlurred
          isZoomed
          alt='Street view of property'
          className={`aspect-video w-full hover:scale-110 ${
            !streetViewUrl ? 'opacity-50' : ''
          }`}
          src={streetViewUrl || '/loginPic.jpg'}
        />
        {!streetViewUrl && (
          <div className='absolute inset-0 z-10 flex items-center justify-center'>
            <div className='rounded-lg bg-content1/80 p-4 text-center'>
              <Spinner size='lg' />
              <p className='mt-2 text-sm font-semibold'>Loading Street View</p>
            </div>
          </div>
        )}
      </div>

      {/* Service Area Alerts */}
      {containingServiceAreaArray &&
        containingServiceAreaArray.length > 0 &&
        containingServiceAreaArray.every(
          (area) => area && area.serviceArea?.expanded
        ) && (
          <Alert color='warning'>
            This property is in an expanded area. Subject to an additional fee.
          </Alert>
        )}
      {containingServiceAreaArray &&
        (containingServiceAreaArray.length === 0 ||
          containingServiceAreaArray.every((area) => !area)) && (
          <Alert color='danger'>
            <p>
              This property is not in our company service area. Continue and we
              will verify if this inspection can be performed.
            </p>
          </Alert>
        )}
      {/* Required Fields Alert */}
      {hasMissingRequiredAddressData && (
        <Alert
          color='danger'
          title={
            hasOutOfRangeRequiredField
              ? 'Check Your Property Details'
              : 'Required Property Details Missing'
          }
        >
          <p>
            {hasOutOfRangeRequiredField ? (
              requiredFieldPrompt.text
            ) : (
              <>
                Please complete the following before continuing:{' '}
                <strong>
                  {formatFieldList(
                    requiredFieldIssues.map((issue) => issue.label)
                  )}
                </strong>
                .
              </>
            )}{' '}
            We use these details to calculate your price.
          </p>
        </Alert>
      )}
      {/* Verification Warnings - Component-ized */}
      {(hasUnverifiedAddressData || hasUnverifiedPropertyDetails) && (
        <VerificationWarnings
          address={address}
          propertyDetails={propertyDetails}
        />
      )}
      {/* Property Information Card */}
      <Card>
        <CardBody className='space-y-6'>
          {/* Property Address & Details Section */}
          <div>
            <h5 className='mb-3 flex items-center gap-2 text-lg font-semibold'>
              {hasMissingRequiredAddressData ? (
                <>
                  <IoWarningOutline className='text-danger' />
                  Property Address & Details
                  <span className='text-xs font-normal text-danger'>
                    (Required)
                  </span>
                </>
              ) : hasUnverifiedAddressData ? (
                <>
                  <IoWarningOutline className='text-warning' />
                  Property Address & Details
                </>
              ) : (
                <>
                  <FaCheckCircle className='text-success' />
                  Property Address & Details
                </>
              )}
            </h5>
            <div className='px-2'>
              <AddressSearchInputFallbackForm
                address={address}
                setAddress={setAddress}
                autoConvertToForm={true}
                canRestart={false}
                hidePropertyDetails={false}
                accessToken={jwtToken}
              />
              {address?.county ? (
                <p className='mt-2 text-sm text-default-500'>
                  County: {address.county}
                </p>
              ) : null}
            </div>
            {hasMissingRequiredAddressData && (
              <div className='mt-3 rounded-lg bg-danger/10 p-3'>
                <p className='text-sm text-danger-700'>
                  <IoWarningOutline className='mr-1 inline text-base' />
                  {requiredFieldPrompt.text}
                </p>
              </div>
            )}
          </div>

          {/* Divider */}
          {/* <div className='border-t' /> */}

          {/* Property Characteristics Section */}
          {/* <div>
            <h5 className='mb-3 flex items-center gap-2 text-lg font-semibold'>
              {hasUnverifiedPropertyDetails ? (
                <>
                  <IoWarningOutline className='text-warning' />
                  Property Characteristics
                  <span className='text-xs font-normal text-gray-500'>
                    (Optional)
                  </span>
                </>
              ) : (
                <>
                  <FaCheckCircle className='text-success' />
                  Property Characteristics
                  <span className='text-xs font-normal text-gray-500'>
                    (Optional)
                  </span>
                </>
              )}
            </h5>
            <form className='grid gap-1 px-2 pb-4 md:grid-cols-3'>

              {PROPERTY_FIELDS.map((field) => (
                <PropertyFormField
                  key={field.name}
                  field={field}
                  register={register}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  setPropertyDetails={setPropertyDetails}
                />
              ))}
            </form>
            {hasUnverifiedPropertyDetails && (
              <div className='mt-3 rounded-lg bg-warning/10 p-3'>
                <p className='text-sm text-warning-700'>
                  <IoWarningOutline className='mr-1 inline text-base' />
                  Some characteristics were auto-populated from Zillow. Please
                  verify or update them as needed.
                </p>
              </div>
            )}
          </div> */}
        </CardBody>
      </Card>

      {/* Zillow Property Details */}
      {/* <ZillowPropertyDetails zillowData={zillowPropertyData} /> */}
      {!zillowPropertyData && (
        <div className='flex items-center justify-center rounded-lg bg-content2 p-8'>
          <div className='text-center'>
            <Spinner size='lg' />
            <p className='mt-3 text-sm text-gray-600'>
              Loading property details from Zillow...
            </p>
          </div>
        </div>
      )}

      {/* Verify and Continue Button */}
      <div className='flex justify-end'>
        <Button
          size='lg'
          onPress={onContinue}
          className={`px-8 font-bold uppercase ${companyStyles.secondaryButtonClassName}`}
          style={companyStyles.secondaryButtonStyles}
          isDisabled={hasMissingRequiredAddressData}
        >
          {hasMissingRequiredAddressData
            ? 'Complete Required Fields'
            : hasUnverifiedAddressData || hasUnverifiedPropertyDetails
            ? 'Verify & Continue'
            : 'Continue to Recommendations'}
        </Button>
      </div>
    </div>
  );
}
